/**
 * Find financial figures in document lines.
 *
 * This is pattern matching, not understanding, so it never decides anything:
 * every match becomes a CANDIDATE showing the document, page and the exact line
 * it came from, and the owner confirms, corrects or rejects it. Where a line
 * has several numbers (one per year, say), each becomes a candidate labelled
 * with its column's year when the page header makes that clear.
 */

import type { Candidate, FinancialField } from '../workspace/schema.js';
import { newId } from '../workspace/schema.js';
import type { DocLine } from './lines.js';

interface Pattern { field: FinancialField; re: RegExp; exclude?: RegExp }

const PATTERNS: Pattern[] = [
  { field: 'cogs', re: /^(total\s+)?cost\s+of\s+(goods\s+sold|sales|revenue)\b/i },
  {
    field: 'annualRevenue',
    re: /^(total\s+)?(net\s+)?(revenues?|sales|gross\s+receipts(\s+or\s+sales)?|gross\s+sales|income\s+from\s+sales)\b/i,
    exclude: /\b(cost|returns|tax|discounts?)\b/i,
  },
  // Form 1120 / 1120-S "Compensation of officers", 1065 "Guaranteed payments to partners".
  { field: 'ownerSalary', re: /^(compensation\s+of\s+officers|officers?'?\s+(compensation|salar(y|ies))|guaranteed\s+payments(\s+to\s+partners)?|owner'?s?\s+(salary|compensation|draw))\b/i },
  { field: 'operatingExpenses', re: /^total\s+((operating\s+)?expenses|deductions)\b/i },
  { field: 'operatingIncome', re: /^(net\s+)?(operating\s+(income|profit)(\s*\(loss\))?|income\s+from\s+operations|ebitda)\b/i },
  { field: 'netIncome', re: /^(net\s+(income|profit|earnings)|net\s+ordinary\s+(business\s+)?income|ordinary\s+business\s+income)\b/i },
  { field: 'interestExpense', re: /^interest(\s+expense)?\b/i, exclude: /\bincome\b/i },
  { field: 'depreciation', re: /^(depreciation(\s+and\s+amortization)?|amortization)\b/i },
  { field: 'existingAnnualDebtService', re: /^(total\s+)?(annual\s+)?((loan|debt|note)s?\s+payments?|debt\s+service)\b/i },
  { field: 'cash', re: /^(total\s+)?cash(\s+and\s+(cash\s+)?equivalents)?\b|^ending\s+balance\b/i, exclude: /\bflow\b/i },
];

/** Strip tax-form line references like "1a", "Line 21", "21." from the start. */
function stripLineRef(s: string): string {
  return s.replace(/^\s*(line\s*)?\d{1,2}[a-z]?[.)]?\s+(?=[a-z])/i, '').trim();
}

/**
 * Remove the references an IRS form puts around its amounts: "Form 1125-A",
 * "Schedule K-1", "lines 7 through 20", and the line number repeated in the
 * right-hand column ("21", "1a"), so only the amount is left.
 */
function stripFormRefs(s: string): string {
  return s
    .replace(/\b(form|schedule)\s+[\w-]+/gi, ' ')
    .replace(/\blines?\s+\d+[a-z]?(\s*(,|through|and|to|from|or)\s*(lines?\s+)?\d+[a-z]?)*/gi, ' ')
    .replace(/(^|\s)\d{1,2}[a-z]?(?=\s+[$(]?-?\d|\s*$)/gi, ' ');
}

const NUMBER = /\(?-?\$?\s?\d{1,3}(?:,\d{3})+(?:\.\d+)?\)?|\(?-?\$?\s?\d+(?:\.\d+)?\)?%?/g;

/** Money-like numbers in a string, in order. Years and percentages are skipped. */
export function moneyIn(s: string): number[] {
  const out: number[] = [];
  for (const m of s.matchAll(NUMBER)) {
    const tok = m[0];
    if (tok.endsWith('%')) continue;
    const neg = tok.startsWith('(') || tok.includes('-');
    const digits = tok.replace(/[()$,\s-]/g, '');
    if (!digits) continue;
    const n = Number(digits);
    if (!Number.isFinite(n)) continue;
    // A bare four-digit year is a heading, not an amount.
    if (/^(19|20)\d{2}$/.test(digits) && !tok.includes('$') && !tok.includes(',')) continue;
    out.push(neg ? -n : n);
  }
  return out;
}

/** Years named in a header line, in column order: "2023  2024", "FY2024", "YTD". */
function yearsIn(s: string): string[] {
  const out: string[] = [];
  for (const m of s.matchAll(/\b(?:FY\s?)?((?:19|20)\d{2})\b|\b(YTD)\b/gi)) out.push(m[1] ?? 'YTD');
  return out;
}

/** The label part of a line: everything before its first number. */
function labelOf(text: string): string {
  const i = text.search(/\(?-?\$?\s?\d/);
  return (i === -1 ? text : text.slice(0, i)).replace(/[.:\s]+$/, '').trim();
}

export function extractCandidates(lines: DocLine[]): Candidate[] {
  const out: Candidate[] = [];
  const seen = new Set<string>();

  // Column years per page/sheet, from the most recent header-looking line.
  let headerYears: string[] = [];
  let lastLoc = '';

  for (const line of lines) {
    const loc = `${line.page ?? ''}|${line.sheet ?? ''}`;
    if (loc !== lastLoc) { headerYears = []; lastLoc = loc; }

    const cellText = line.cells
      ? line.cells.map((c) => (typeof c === 'number' ? c.toLocaleString('en-US', { useGrouping: true }) : c)).join('    ')
      : line.text;
    const years = yearsIn(cellText);
    // Strip a tax-form line reference ("1a", "Line 21") BEFORE finding the
    // label, or a line that starts with one would look like it has no label.
    const body = line.cells ? '' : stripLineRef(line.text);
    const label = line.cells
      ? stripLineRef(String(line.cells.find((c) => typeof c === 'string' && /[a-z]/i.test(c)) ?? ''))
      : labelOf(body);

    // A line that is mostly years is a column header.
    if (years.length >= 1 && (!label || /^(fy|year|period|description|account|item)?$/i.test(label))) {
      headerYears = years;
      continue;
    }
    if (!label) continue;

    const pat = PATTERNS.find((p) => p.re.test(label) && !(p.exclude && p.exclude.test(label)));
    if (!pat) continue;

    // A tax-form line ("13 Interest ... 13 12,900") carries one amount, the
    // last number, after form and line references are taken out.
    const isFormLine = !line.cells && body !== line.text.trim();
    const values = line.cells
      ? line.cells.filter((c): c is number => typeof c === 'number' && Number.isFinite(c))
      : isFormLine ? moneyIn(stripFormRefs(body.slice(label.length))).slice(-1)
      : moneyIn(body.slice(label.length));
    if (values.length === 0) continue;

    values.slice(0, 4).forEach((v, i) => {
      const period = values.length > 1 && headerYears.length === values.length
        ? headerYears[i]
        : values.length === 1 && headerYears.length === 1 ? headerYears[0] : undefined;
      const key = `${pat.field}|${v}|${line.page ?? line.sheet}|${period ?? ''}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push({
        id: newId('c'),
        field: pat.field,
        value: v,
        label,
        snippet: cellText.length > 160 ? `${cellText.slice(0, 157)}...` : cellText,
        ...(line.page !== undefined ? { page: line.page } : {}),
        ...(line.sheet !== undefined ? { sheet: line.sheet } : {}),
        ...(period ? { period } : {}),
        state: 'pending',
      });
    });
  }
  return out;
}

/**
 * A text-based PDF has a text layer; a scan or photo does not. Below this many
 * characters per page there is nothing reliable to read without OCR.
 */
export function looksScanned(totalChars: number, pages: number): boolean {
  return pages > 0 && totalChars / pages < 40;
}
